import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const WIDTH = 800;
const HEIGHT = 400;
const MARGIN = { top: 20, right: 30, bottom: 40, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: { fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '4px' },
  subtitle: { fontSize: '12px', color: '#9ca3af', marginBottom: '20px' },
  toggleRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  toggleBtn: {
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 500,
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    background: '#fff',
    color: '#6b7280',
    transition: 'all 0.15s',
  },
  toggleBtnActive: {
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 600,
    border: '1px solid #2563eb',
    borderRadius: '6px',
    cursor: 'pointer',
    background: '#eff6ff',
    color: '#2563eb',
  },
  benchmarkPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 500,
    color: '#166534',
    marginBottom: '16px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '3px',
  },
  tooltipLabel: {
    color: '#6b7280',
  },
  tooltipValue: {
    fontWeight: 600,
    color: '#1f2937',
  },
};

function getIRColor(ir) {
  if (ir > 0.5) return '#16a34a';
  if (ir >= 0) return '#f59e0b';
  return '#dc2626';
}

export default function InformationRatioChart({ data }) {
  const svgRef = useRef(null);
  const [view, setView] = useState('state');
  const [tooltip, setTooltip] = useState(null);

  // State Comparison view
  useEffect(() => {
    if (!data || !data.states || data.states.length === 0) return;
    if (view !== 'state') return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const sorted = [...data.states].sort((a, b) => a.informationRatio - b.informationRatio);

    const innerW = WIDTH - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    // Y scale: states
    const y = d3.scaleBand()
      .domain(sorted.map((d) => d.state))
      .range([0, innerH])
      .padding(0.25);

    // X scale: IR values
    const xMin = d3.min(sorted, (d) => d.informationRatio);
    const xMax = d3.max(sorted, (d) => d.informationRatio);
    const pad = Math.max(Math.abs(xMax - xMin) * 0.15, 0.3);

    const x = d3.scaleLinear()
      .domain([Math.min(xMin - pad, -0.3), Math.max(xMax + pad, 0.8)])
      .nice()
      .range([0, innerW]);

    // Grid lines
    g.selectAll('.grid')
      .data(x.ticks(8))
      .enter()
      .append('line')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero line
    g.append('line')
      .attr('x1', x(0))
      .attr('x2', x(0))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    // Bars
    sorted.forEach((d) => {
      const color = getIRColor(d.informationRatio);
      const barY = y(d.state);
      const barH = y.bandwidth();
      const barStart = x(0);
      const barEnd = x(d.informationRatio);
      const barX = Math.min(barStart, barEnd);
      const barW = Math.abs(barEnd - barStart);

      const row = g.append('g').style('cursor', 'pointer');

      row.append('rect')
        .attr('x', barX)
        .attr('y', barY)
        .attr('width', barW)
        .attr('height', barH)
        .attr('fill', color)
        .attr('rx', 3)
        .attr('opacity', 0.85);

      // Value label
      const labelX = d.informationRatio >= 0 ? barEnd + 6 : barEnd - 6;
      const anchor = d.informationRatio >= 0 ? 'start' : 'end';

      row.append('text')
        .attr('x', labelX)
        .attr('y', barY + barH / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', anchor)
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', color)
        .text(d.informationRatio.toFixed(2));

      // Hover target
      row.append('rect')
        .attr('x', 0)
        .attr('y', barY)
        .attr('width', innerW)
        .attr('height', barH)
        .attr('fill', 'transparent')
        .on('mouseenter', (event) => {
          row.select('rect').attr('opacity', 1);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            state: d.state,
            informationRatio: d.informationRatio,
            excessReturn: d.excessReturn,
            trackingError: d.trackingError,
            addressCount: d.addressCount,
            avgPrice: d.avgPrice,
            benchmarkAvg: d.benchmarkAvg,
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) =>
            prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : prev
          );
        })
        .on('mouseleave', () => {
          row.select('rect').attr('opacity', 0.85);
          setTooltip(null);
        });
    });

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .call((a) => a.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .attr('font-weight', 500);

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(8)
          .tickSize(0)
          .tickFormat((d) => d.toFixed(1))
      )
      .call((a) => a.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 34)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Information Ratio');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, view]);

  // Rolling IR view
  useEffect(() => {
    if (!data || !data.rollingIR || data.rollingIR.length === 0) return;
    if (view !== 'rolling') return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = WIDTH - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const points = data.rollingIR.map((d) => ({
      date: new Date(d.date),
      ir: d.ir,
    }));

    // X scale: time
    const x = d3.scaleTime()
      .domain(d3.extent(points, (d) => d.date))
      .range([0, innerW]);

    // Y scale: IR values
    const yMin = d3.min(points, (d) => d.ir);
    const yMax = d3.max(points, (d) => d.ir);
    const yPad = Math.max(Math.abs(yMax - yMin) * 0.2, 0.3);

    const y = d3.scaleLinear()
      .domain([Math.min(yMin - yPad, -0.8), Math.max(yMax + yPad, 0.8)])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid
    g.selectAll('.grid-h')
      .data(y.ticks(8))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Reference lines at 0 and +/-0.5
    const refLines = [
      { value: 0, color: '#374151', label: '0' },
      { value: 0.5, color: '#16a34a', label: '+0.5' },
      { value: -0.5, color: '#dc2626', label: '-0.5' },
    ];

    refLines.forEach((ref) => {
      const ry = y(ref.value);
      if (ry >= 0 && ry <= innerH) {
        g.append('line')
          .attr('x1', 0)
          .attr('x2', innerW)
          .attr('y1', ry)
          .attr('y2', ry)
          .attr('stroke', ref.color)
          .attr('stroke-width', ref.value === 0 ? 1.5 : 1)
          .attr('stroke-dasharray', ref.value === 0 ? '6,3' : '4,4')
          .attr('stroke-opacity', 0.7);

        g.append('text')
          .attr('x', innerW + 4)
          .attr('y', ry)
          .attr('dy', '0.35em')
          .attr('font-size', '9px')
          .attr('fill', ref.color)
          .attr('font-weight', 500)
          .text(ref.label);
      }
    });

    // Area fill for positive/negative
    const areaAbove = d3.area()
      .x((d) => x(d.date))
      .y0(y(0))
      .y1((d) => d.ir >= 0 ? y(d.ir) : y(0))
      .curve(d3.curveMonotoneX);

    const areaBelow = d3.area()
      .x((d) => x(d.date))
      .y0(y(0))
      .y1((d) => d.ir < 0 ? y(d.ir) : y(0))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(points)
      .attr('d', areaAbove)
      .attr('fill', '#16a34a')
      .attr('fill-opacity', 0.08);

    g.append('path')
      .datum(points)
      .attr('d', areaBelow)
      .attr('fill', '#dc2626')
      .attr('fill-opacity', 0.08);

    // Line
    const line = d3.line()
      .x((d) => x(d.date))
      .y((d) => y(d.ir))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(points)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2);

    // Dots
    g.selectAll('.dot')
      .data(points)
      .enter()
      .append('circle')
      .attr('cx', (d) => x(d.date))
      .attr('cy', (d) => y(d.ir))
      .attr('r', 3)
      .attr('fill', (d) => getIRColor(d.ir))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);

    // Hover overlay for rolling line
    const bisect = d3.bisector((d) => d.date).left;

    const focus = g.append('g').style('display', 'none');

    focus.append('circle')
      .attr('r', 5)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2);

    focus.append('line')
      .attr('class', 'focus-line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3');

    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .on('mouseenter', () => focus.style('display', null))
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const dateAtMouse = x.invert(mx);
        const idx = bisect(points, dateAtMouse, 1);
        const d0 = points[idx - 1];
        const d1 = points[idx];
        if (!d0) return;
        const d = d1 && (dateAtMouse - d0.date > d1.date - dateAtMouse) ? d1 : d0;

        focus.attr('transform', `translate(${x(d.date)},${y(d.ir)})`);
        focus.select('.focus-line')
          .attr('y1', -y(d.ir))
          .attr('y2', innerH - y(d.ir));

        const fmtDate = d3.timeFormat('%b %d, %Y');
        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          rollingDate: fmtDate(d.date),
          rollingIR: d.ir,
        });
      })
      .on('mouseleave', () => {
        focus.style('display', 'none');
        setTooltip(null);
      });

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(6)
          .tickSize(0)
          .tickFormat(d3.timeFormat('%b %Y'))
      )
      .call((a) => a.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(8)
          .tickSize(0)
          .tickFormat((d) => d.toFixed(1))
      )
      .call((a) => a.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -44)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Information Ratio');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, view]);

  // Empty state
  if (!data || !data.states || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Information Ratio Analysis</div>
        <div style={styles.subtitle}>Risk-adjusted performance relative to national benchmark</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No information ratio data available
        </div>
      </div>
    );
  }

  const hasRolling = data.rollingIR && data.rollingIR.length > 0;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Information Ratio Analysis</div>
      <div style={styles.subtitle}>Risk-adjusted performance relative to national benchmark</div>

      {/* Benchmark pill */}
      {data.benchmark && (
        <div style={styles.benchmarkPill}>
          <span style={{ fontSize: '13px' }}>&#9679;</span>
          <span>
            Benchmark: {data.benchmark.label || data.benchmark.type}
            {data.benchmark.value != null && ` ($${data.benchmark.value.toFixed(2)})`}
          </span>
          {data.field && (
            <span style={{ color: '#15803d', fontStyle: 'italic' }}>
              | {data.field}
            </span>
          )}
        </div>
      )}

      {/* View toggle */}
      {hasRolling && (
        <div style={styles.toggleRow}>
          <button
            style={view === 'state' ? styles.toggleBtnActive : styles.toggleBtn}
            onClick={() => setView('state')}
          >
            State Comparison
          </button>
          <button
            style={view === 'rolling' ? styles.toggleBtnActive : styles.toggleBtn}
            onClick={() => setView('rolling')}
          >
            Rolling IR
          </button>
        </div>
      )}

      {/* Chart */}
      <div style={styles.container}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          style={{ width: '100%', height: 'auto' }}
        />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          {tooltip.state ? (
            <>
              <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>
                {tooltip.state}
              </div>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>Information Ratio</span>
                <span style={{ fontWeight: 600, color: getIRColor(tooltip.informationRatio) }}>
                  {tooltip.informationRatio.toFixed(2)}
                </span>
              </div>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>Excess Return</span>
                <span style={styles.tooltipValue}>
                  {tooltip.excessReturn != null
                    ? `${tooltip.excessReturn >= 0 ? '+' : ''}${(tooltip.excessReturn * 100).toFixed(1)}%`
                    : '--'}
                </span>
              </div>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>Tracking Error</span>
                <span style={styles.tooltipValue}>
                  {tooltip.trackingError != null ? tooltip.trackingError.toFixed(3) : '--'}
                </span>
              </div>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>Avg Price</span>
                <span style={styles.tooltipValue}>
                  {tooltip.avgPrice != null ? `$${tooltip.avgPrice.toFixed(2)}` : '--'}
                </span>
              </div>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>Benchmark Avg</span>
                <span style={styles.tooltipValue}>
                  {tooltip.benchmarkAvg != null ? `$${tooltip.benchmarkAvg.toFixed(2)}` : '--'}
                </span>
              </div>
              <div style={{ ...styles.tooltipRow, borderTop: '1px solid #f3f4f6', paddingTop: '4px', marginTop: '2px' }}>
                <span style={styles.tooltipLabel}>Addresses</span>
                <span style={styles.tooltipValue}>
                  {tooltip.addressCount != null ? tooltip.addressCount : '--'}
                </span>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                {tooltip.rollingDate}
              </div>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>IR</span>
                <span style={{ fontWeight: 600, color: getIRColor(tooltip.rollingIR) }}>
                  {tooltip.rollingIR.toFixed(2)}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
